// Angular
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  input,
  effect,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Angular Material
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';

// Third Party
import { finalize, tap } from 'rxjs';
import Papa from 'papaparse';

// Services
import { IntegrationService } from '@services/integration.service';

// Models
import { Integration } from '@components/integration';
import { Active, Action } from '@constants/enum';
import { CustomPaginator } from '@utils/custom-paginator';

// Pipes
import { TooltipCustomerInfoPipe } from '@utils/tooltip.pipe';

// Components
import { LoadingSpinnerComponent, FormErrorComponent } from '@components/common-components';

@Component({
  selector: 'app-operational-api-table',
  imports: [
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatSidenavModule,
    MatTooltipModule,
    RouterLink,
    TooltipCustomerInfoPipe,
    LoadingSpinnerComponent,
    FormErrorComponent,
  ],
  templateUrl: './integration-table.component.html',
  styleUrl: './integration-table.component.scss',
  providers: [{ provide: MatPaginatorIntl, useValue: CustomPaginator() }],
})
export class IntegrationTableComponent implements OnInit {
  constructor() {
    effect(() => {
      this.search.set(this.searchTerm());
      this.pageIndex.set(0);
    });
  }

  // Component Inputs
  readonly integrationCategory = input<string>('');
  readonly searchTerm = input<string>('');

  private integrationService = inject(IntegrationService);
  private destroyRef = inject(DestroyRef);

  displayedColumns = [
    'vendorName',
    'vendorKey',
    'integrationType',
    'pushUrl',
    'customers',
    'status',
    'actions',
  ];

  readonly defaultData = signal<Integration[]>([]);

  readonly search = signal('');
  readonly status = signal<string[]>([]);
  readonly sort = signal<Sort>({ active: 'vendorName', direction: 'asc' });

  statusList: string[] = [Active.active, Active.inactive];

  readonly pageSize = signal(50);
  readonly pageIndex = signal(0);

  readonly loading = signal(true);
  readonly error = signal(false);

  readonly action = Action;

  readonly filteredData = computed(() => {
    let data = this.defaultData();

    if (data.some((value) => value === null)) {
      return [];
    }

    // Filter on status
    if (this.status().includes(Active.active) && this.status().includes(Active.inactive)) {
      // both selected — show all
    } else if (this.status().includes(Active.active)) {
      data = data.filter((element) => element.enabled == true);
    } else if (this.status().includes(Active.inactive)) {
      data = data.filter((element) => element.enabled == false);
    }

    // Search
    const searchTerm = this.search().toLowerCase();
    if (searchTerm) {
      data = data.filter(
        (element) =>
          element.vendorName?.toLowerCase().includes(searchTerm) ||
          element.vendorKey?.toLowerCase().includes(searchTerm) ||
          element.pushUrl?.toLowerCase().includes(searchTerm) ||
          element.integrationType?.toLowerCase().includes(searchTerm) ||
          element.customers?.find((x) => x.customerKey?.includes(searchTerm)),
      );
    }

    // Sort
    return this.sortData(this.sort(), data);
  });

  readonly resultsLength = computed(() => this.filteredData().length);

  readonly sortedData = computed(() => {
    const data = this.filteredData();
    return data.slice(this.pageIndex() * this.pageSize(), (this.pageIndex() + 1) * this.pageSize());
  });

  ngOnInit() {
    this.getIntegrations(this.integrationCategory());
  }

  getIntegrations(integrationCategory: string) {
    this.loading.set(true);
    this.integrationService
      .getIntegrationList(integrationCategory)
      .pipe(
        tap((data: Integration[]) => {
          if (data.some((value) => value === null)) {
            this.error.set(true);
          }
          this.defaultData.set(data);
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: () => this.error.set(true),
      });
  }

  selectSort(sort: Sort) {
    this.sort.set(sort);
  }

  sortData(sort: Sort, dataToBeSorted: Integration[]) {
    const data = dataToBeSorted.slice();
    if (!sort.active || sort.direction === '') {
      return data;
    }
    return data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      const isDesc = sort.direction === 'desc';
      switch (sort.active) {
        case 'vendorName':
          return this.compare(a.vendorName.toLowerCase(), b.vendorName.toLowerCase(), isAsc);
        case 'vendorKey':
          return this.compare(a.vendorKey.toLowerCase(), b.vendorKey.toLowerCase(), isAsc);
        case 'pushUrl':
          return this.compare(a.pushUrl.toLowerCase(), b.pushUrl.toLowerCase(), isAsc);
        case 'integrationType':
          return this.compare(
            a.integrationType.toLowerCase(),
            b.integrationType.toLowerCase(),
            isAsc,
          );
        case 'status':
          return this.compare(a.enabled, b.enabled, isDesc);
        default:
          return 0;
      }
    });
  }

  compare(
    a: number | string | Date | boolean,
    b: number | string | Date | boolean,
    isAsc: boolean,
  ) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  searchTable(search: string) {
    this.search.set(search);
    this.pageIndex.set(0);
  }

  handlePageEvent(event: PageEvent) {
    this.pageSize.set(event.pageSize);
    this.pageIndex.set(event.pageIndex);
  }

  exportData() {
    const now = new Date();
    const fileName = `operational_api_${now.getDate()}_${now.getMonth() + 1}_${now.getFullYear()}.csv`;
    const blob = new Blob([this.ConvertToCSV(this.sortedData())], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();

    URL.revokeObjectURL(url);
  }

  ConvertToCSV(integrations: Integration[]): string {
    const ignored = [
      'apiKey',
      'apiKeyHeaderName',
      'authEndpointUrl',
      'authUsername',
      'authPassword',
      'oauthClientId',
      'oauthClientSecret',
      'oauthGrantType',
      'frequency',
      'eventTypes',
    ];

    const fields = Object.keys(integrations[0]).filter((key) => !ignored.includes(key));

    const transformed = integrations.map((integration) => {
      const row: Record<string, string> = {};
      for (const field of fields) {
        let value: string;
        if (field === 'contactInformation') {
          const contact = integration[field];
          value = `${contact.contactName} : ${contact.contactEmail} : ${contact.contactPhone}`;
        } else if (field === 'customers') {
          value = (integration[field] ?? []).map((c) => c.customerKey).join('; ');
        } else if (field === 'authType') {
          value = integration[field].authMethod;
        } else {
          value = String(integration[field] ?? '');
        }
        row[field] = value;
      }
      return row;
    });

    return Papa.unparse(transformed, { columns: fields });
  }
}
